import React from "react";
import { Button } from "reactstrap";

/**
 * Catches any render/lifecycle error below it and shows a readable message
 * instead of a blank page.
 *
 * React unmounts the whole tree when a render throws, so without a boundary a
 * single bad field white-screens the entire Admin Site with nothing on screen
 * to explain it — which is exactly how the Terms/Privacy pages failed (reading
 * `.privacy` off a `false` response threw a TypeError). Operators should always
 * be told what happened and given a way out.
 *
 * Deliberately dependency-free and defensive: a boundary that throws while
 * rendering its own fallback takes the page down with it.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      // Kept for the operator to quote in a bug report; never rendered as HTML.
      message: (error && error.message) || "Unexpected error",
    };
  }

  componentDidCatch(error, errorInfo) {
    // No error-reporting service is wired up in the Admin Site, so the console
    // is the only trail. Keep it — it's what a developer will ask for.
    // eslint-disable-next-line no-console
    console.error("Admin Site error:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    // Full navigation, not a router push: the router tree is the thing that
    // just threw, so re-entering it in place can immediately throw again.
    window.location.href = "/dashboard";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "520px" }}>
          <h3 style={{ marginBottom: "8px" }}>Something went wrong</h3>
          <p style={{ color: "#6c757d", marginBottom: "4px" }}>
            This page ran into an unexpected problem and couldn&apos;t be displayed.
            Your data has not been changed.
          </p>
          <p style={{ color: "#adb5bd", fontSize: "12px", marginBottom: "20px" }}>
            {this.state.message}
          </p>
          <Button color="primary" onClick={this.handleReload} style={{ marginRight: "8px" }}>
            Reload this page
          </Button>
          <Button color="secondary" outline onClick={this.handleHome}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
